// src/pages/MeetingInvite.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";

const MeetingInvite = () => {
  const [loadingUser, setLoadingUser] = useState(true);
  const [systemRole, setSystemRole] = useState("");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const [form, setForm] = useState({
    title: "",
    date: "",
    time: "",
    platform: "Zoom",
    meeting_link: "",
    agenda: "",
    send_to_all: false,
    selected_user_ids: [],
  });

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  /* ---------------- LOAD USER ---------------- */
  useEffect(() => {
    const fetchMe = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const res = await axios.get("/api/users/me/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSystemRole(res.data.system_role);
      } catch {
        setError("Unable to load user details.");
      } finally {
        setLoadingUser(false);
      }
    };
    fetchMe();
  }, []);

  /* ---------------- LOAD USERS ---------------- */
  useEffect(() => {
    if (systemRole !== "Head") return;

    const fetchCompanyUsers = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const res = await axios.get("/api/meetings/company-users/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(res.data);
      } catch {
        setUsers([]);
      }
    };
    fetchCompanyUsers();
  }, [systemRole]);

  /* ---------------- LOAD ROLES ---------------- */
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const res = await axios.get("/api/meetings/roles/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRoles(res.data);
      } catch {
        setRoles([]);
      }
    };
    fetchRoles();
  }, []);

  /* ---------------- FILTER USERS ---------------- */
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const searchMatch =
        u.username.toLowerCase().includes(search.toLowerCase()) ||
        (u.email || "").toLowerCase().includes(search.toLowerCase());
      const roleMatch = roleFilter ? u.role === roleFilter : true;
      return searchMatch && roleMatch;
    });
  }, [users, search, roleFilter]);

  /* ---------------- FORM HANDLERS ---------------- */
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "send_to_all" ? checked : value,
    }));
  };

  const toggleUser = (id) => {
    setForm((prev) => {
      const set = new Set(prev.selected_user_ids);
      set.has(id) ? set.delete(id) : set.add(id);
      return { ...prev, selected_user_ids: [...set] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!form.title || !form.date || !form.time || !form.meeting_link) {
      setError("Please fill all required fields.");
      return;
    }

    if (!form.send_to_all && form.selected_user_ids.length === 0) {
      setError("Select users or choose Send To All.");
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem("accessToken");

      await axios.post(
        "/api/meetings/create/",
        {
          ...form,
          selected_user_ids: form.send_to_all ? [] : form.selected_user_ids,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage("Meeting invites sent successfully!");
      setForm({
        ...form,
        title: "",
        date: "",
        time: "",
        meeting_link: "",
        agenda: "",
        selected_user_ids: [],
      });
    } catch {
      setError("Failed to send invites.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingUser) return <div className="centerBox">Loading...</div>;

  if (systemRole !== "Head")
    return (
      <div className="centerBox">
        <div className="deniedBox">
          <h2>Access Denied</h2>
          Only Head can send invites.
        </div>
      </div>
    );

  return (
    <div className="meetingPage">
      <div className="headerRow">
        <h1 className="pageTitle">Send ISO Mandated Meeting Invite</h1>
        <button className="backBtn" onClick={() => (window.location.href = "/policy-review")}>
          ⬅ Back
        </button>
      </div>

      <div className="formCard">
        <form onSubmit={handleSubmit}>
          {/* MEETING FORM */}
          <div className="grid">
            <div>
              <label>Meeting Title *</label>
              <input className="modernInput" name="title" value={form.title} onChange={handleChange} />
            </div>

            <div>
              <label>Date *</label>
              <input type="date" className="modernInput" name="date" value={form.date} onChange={handleChange} />
            </div>

            <div>
              <label>Time *</label>
              <input type="time" className="modernInput" name="time" value={form.time} onChange={handleChange} />
            </div>

            <div>
              <label>Platform</label>
              <select className="modernInput" name="platform" value={form.platform} onChange={handleChange}>
                <option>Zoom</option>
                <option>Microsoft Teams</option>
                <option>Google Meet</option>
                <option>Webex</option>
              </select>
            </div>

            <div className="colSpan2">
              <label>Meeting Link *</label>
              <input className="modernInput" name="meeting_link" value={form.meeting_link} onChange={handleChange} />
            </div>

            <div className="colSpanFull">
              <label>Agenda (optional)</label>
              <textarea className="modernInput textarea" name="agenda" value={form.agenda} onChange={handleChange} />
            </div>
          </div>

          {/* RECIPIENTS */}
          <h3 className="sectionTitle">Recipients</h3>

          <label className="checkRow">
            <input type="checkbox" name="send_to_all" checked={form.send_to_all} onChange={handleChange} />
            <span>Send to all company users</span>
          </label>

          {!form.send_to_all && (
            <div className="recipientBox">
              <div className="filterRow">
                <input
                  type="text"
                  className="modernInput search"
                  placeholder="Search username or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                <select className="modernInput roleSelect" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                  <option value="">All Roles</option>
                  {roles.map((r) => (
                    <option key={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div className="userList">
                {filteredUsers.length === 0 ? (
                  <div className="emptyMsg">No users found.</div>
                ) : (
                  filteredUsers.map((u) => (
                    <label key={u.id} className="userRow modernUserRow">
                      <input
                        type="checkbox"
                        checked={form.selected_user_ids.includes(u.id)}
                        onChange={() => toggleUser(u.id)}
                      />
                      <span>
                        {u.username} — {u.role || "No Role"} ({u.email})
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {message && <div className="successBox">{message}</div>}
          {error && <div className="errorBox">{error}</div>}

          <button className="submitBtn" disabled={submitting}>
            {submitting ? "Sending..." : "Send Meeting Invite"}
          </button>
        </form>
      </div>

      <style>{`
        .meetingPage {
          padding: 40px;
          background: linear-gradient(135deg, #f6f8fb, #e8edf7);
          min-height: 100vh;
          font-family: "Segoe UI", Roboto, sans-serif;
        }
        .headerRow {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 25px;
        }
        .pageTitle {
          font-size: 28px;
          color: #0f1d60;
          font-weight: 700;
        }
        .backBtn {
          background: #0f1d60;
          color: white;
          padding: 10px 16px;
          border-radius: 10px;
          border: none;
          cursor: pointer;
          transition: 0.3s;
        }
        .backBtn:hover {
          background: #1b2f8b;
          transform: scale(1.05);
        }

        .formCard {
          background: white;
          padding: 30px 35px;
          border-radius: 16px;
          box-shadow: 0 6px 20px rgba(0,0,0,0.1);
          max-width: 900px;
          margin: auto;
        }
        .grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
        }
        .colSpan2 { grid-column: span 2; }
        .colSpanFull { grid-column: 1 / -1; }

        label {
          font-weight: 600;
          color: #0f1d60;
        }
        .modernInput {
          background: #fafafa;
          border: 1px solid #ccc;
          border-radius: 10px;
          padding: 10px 12px;
          font-size: 15px;
          width: 100%;
          transition: all 0.2s ease;
        }
        .modernInput:focus {
          border-color: #2563eb;
          background: #fff;
          box-shadow: 0 0 6px rgba(37,99,235,0.2);
          outline: none;
        }
        .textarea {
          min-height: 100px;
          resize: vertical;
        }

        .sectionTitle {
          margin-top: 25px;
          margin-bottom: 10px;
          font-size: 18px;
          color: #0f1d60;
          font-weight: 600;
        }
        .checkRow {
          display: flex;
          gap: 10px;
          margin-bottom: 15px;
          align-items: center;
        }
        .recipientBox {
          background: #f9fbff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 18px;
          box-shadow: inset 0 2px 6px rgba(0,0,0,0.03);
        }
        .filterRow {
          display: flex;
          gap: 10px;
          margin-bottom: 10px;
          flex-wrap: wrap;
        }
        .userList {
          max-height: 250px;
          overflow-y: auto;
          padding-right: 6px;
        }
        .modernUserRow {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 8px;
          border-radius: 8px;
          transition: background 0.2s;
        }
        .modernUserRow:hover {
          background: #eef2ff;
        }
        .emptyMsg {
          text-align: center;
          color: #6b7280;
          padding: 10px;
        }

        .successBox {
          background: #dcfce7;
          color: #065f46;
          padding: 12px;
          border-radius: 10px;
          margin-top: 15px;
        }
        .errorBox {
          background: #fee2e2;
          color: #991b1b;
          padding: 12px;
          border-radius: 10px;
          margin-top: 15px;
        }

        .submitBtn {
          margin-top: 25px;
          width: 100%;
          padding: 14px;
          background: #0f1d60;
          color: white;
          font-weight: 600;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          transition: background 0.3s ease, transform 0.2s;
        }
        .submitBtn:hover {
          background: #1b2f8b;
          transform: scale(1.02);
        }

        .centerBox {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          font-family: "Segoe UI", Roboto, sans-serif;
        }
        .deniedBox {
          background: white;
          padding: 30px;
          border-radius: 14px;
          text-align: center;
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }
      `}</style>
    </div>
  );
};

export default MeetingInvite;
